import { useEffect, useMemo, useState } from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useAuth0 } from '@auth0/auth0-react';

// Slightly longer than LDProvider's own 5s timeout — fallback for when LD is
// unreachable and no cached flags are available.
const LD_READY_TIMEOUT_MS = 7000;

/**
 * Feature flag keys used in PSP.
 * Add new feature flags here as they are created in LaunchDarkly.
 *
 * Naming convention: psp-<area>-<feature-name>
 * Example: psp-ui-new-navigation
 */
export enum FeatureFlagKey {
  // Example flags - replace with actual flags from LaunchDarkly
  // EXAMPLE_FEATURE = 'psp-ui-example-feature',
  PSP_CC_PERSONA_SWITCHER = 'psp-command-center-persona-switcher',
  PSP_CC_TASK_CARD_REFRESH_BUTTON = 'psp-command-center-task-card-refresh-button',
  PSP_CC_ENABLE_TASK_CARD = 'psp-command-center-enable-task-card',
  // When true, render the new remotely-hosted Command Center bundle in place of
  // the in-shell `CommandCenterPage`. See CommandCenterRouter.tsx.
  PSP_COMMAND_CENTER_REMOTE_HOSTED = 'psp-command-center-remote-hosted',
  PSP_DEMO_ASSISTANT_WIDGET = 'psp-demo-assistant-widget',
  PSP_DEMO_ASSISTANT_APP = 'psp-demo-assistant-app',
  // Gates the Government OS remote app in "Your Action Hub". Off (or undefined in
  // LaunchDarkly) hides it everywhere; on shows it with no entitlement required.
  PSP_UI_GOVERNMENT_OS = 'psp-ui-government-os',
  PSP_UI_OG_ASSIST = 'psp-ui-og-assist',
  PSP_UI_NOTIFICATIONS = 'psp-ui-notifications',
  PSP_UI_LSF_PAGE_REFRESH = 'psp-ui-lsf-page-refresh',
  // Gates the first-run /onboarding route redirect.
  PSP_UI_ONBOARDING_EXPERIENCE = 'psp-ui-onboarding-experience',
  PSP_DEV_TOOLS = 'psp-ui-dev-tools',
  PSP_ANALYTICS_SEGMENT = 'psp-analytics-segment',
  // Gates @opengov/analytics (PostHog). Off creates no client — no pageviews,
  // identify, autocapture, or `shell.analytics` for mounted modules.
  PSP_OG_ANALYTICS = 'psp-og-analytics',
  // Shows opengovstatus.com incidents and maintenance in PSP's banner slot.
  // Off (or undefined in LaunchDarkly) shows none; PSP's own and mounted
  // modules' LaunchDarkly announcements are unaffected. See PspAnnouncements.tsx.
  PSP_UI_STATUS_PAGE_ANNOUNCEMENTS = 'psp-ui-status-page-announcements',
  PSP_UI_AHA_FEEDBACK = 'psp-ui-aha-feedback',
  PSP_UI_APPS_UNDER_CONSTRUCTION = 'psp-ui-apps-under-construction',
  PSP_UI_SUBSIDIARY_LEDGER = 'psp-ui-subsidiary-ledger',
  PSP_UI_PROJECT_ACCOUNTING = 'psp-ui-project-accounting',
  PSP_UI_AUDIT_LOGS = 'psp-ui-audit-logs',
  PSP_UI_PAYROLL = 'psp-ui-payroll',
  PSP_UI_CORE_HR = 'psp-ui-core-hr',
  PSP_UI_WORKFORCE_PLANNING = 'psp-ui-workforce-planning',
  PSP_UI_LMS = 'psp-ui-lms',
  PSP_ENABLE_BUDGETING = 'psp-enable-budgeting',
  PSP_UI_FAVORITES = 'psp-ui-favorites',
  PSP_UI_SITES = 'psp-ui-sites',
  // Gates the All Products page (/all-products) and its "View all products"
  // sidebar link. See AllProductsPage.tsx and NavigationSection.tsx.
  PSP_UI_ALL_PRODUCTS_PAGE = 'psp-ui-all-products-page',
  // Per-community gate for mounting PLC in-shell in prod vs tab-out. See CIT-23005.
  PSP_ENABLE_PLC = 'psp-enable-plc',
  // Per-community gate for mounting App Builder (gab-core, v2) in-shell vs.
  // the legacy v1 iframe. Every environment, including dev, is flag-gated
  // identically via appBuilderMountsInShell (off by default, keeping the v1
  // iframe until enabled). See PSS-2932.
  PSP_ENABLE_APP_BUILDER_V2 = 'psp-enable-app-builder-v2',
  // Gates the sidebar entries built at runtime from an entity's gab-core app entitlements.
  // See services/gabDynamicAppConfigs.ts.
  PSP_UI_DYNAMIC_GAB_APPS = 'psp-ui-dynamic-gab-apps',
  // Gates the Community Feedback in-shell app at /engagement.
  PSP_ENABLE_COMMUNITY_FEEDBACK_2 = 'psp-community-feedback-2',
  // Gates shell-owned NavBar rendering from remote `remoteNav` contributions.
  // See ShellRemoteNavBar.tsx / unified top nav Phase 1+.
  PSP_UI_SHELL_REMOTE_NAV = 'psp-ui-shell-remote-nav',
}

const warnedKeys = new Set<FeatureFlagKey>();

function warnIfUndefined(
  flagKey: FeatureFlagKey,
  ldFlagValue: unknown,
  ldFlagsReady: boolean
): void {
  if (ldFlagValue !== undefined) return;
  // Suppress during LD bootstrap. useFlags() returns {} until the client
  // finishes initializing; warning then would fire once per known flag on
  // every cold load. Only warn after LD has responded with at least one
  // flag — at that point a missing key is a real config/typo signal.
  if (!ldFlagsReady) return;
  if (warnedKeys.has(flagKey)) return;
  warnedKeys.add(flagKey);
  console.warn(
    `[FeatureFlags] LaunchDarkly returned undefined for "${flagKey}" — defaulting to false. ` +
      `Likely causes: (1) flag does not exist in LaunchDarkly, or (2) flag key is misspelled. ` +
      `Verify at https://app.launchdarkly.com and that VITE_LAUNCHDARKLY_CLIENT_SIDE_ID is set for this environment.`
  );
}

/**
 * Type-safe feature flags object.
 * Maps flag keys to their expected return types (boolean or number).
 */
export type FeatureFlags = {
  [K in FeatureFlagKey]: boolean | number;
};

/**
 * Type guard to check if a value is a number.
 * Used for runtime validation of feature flag types.
 */
function isNumber<DefaultType>(value: DefaultType): value is DefaultType & number {
  return typeof value === 'number';
}

/**
 * Hook to access LaunchDarkly feature flags in a type-safe way.
 *
 * Returns an object with all feature flags defined in FeatureFlagKey enum.
 * If LaunchDarkly is not initialized or a flag doesn't exist, returns false as default.
 * For local overrides, use the LaunchDarkly Developer Toolbar (visible bottom-right in dev).
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const flags = useFeatureFlags();
 *
 *   if (flags[FeatureFlagKey.EXAMPLE_FEATURE]) {
 *     return <NewFeature />;
 *   }
 *
 *   return <OldFeature />;
 * }
 * ```
 */
export const useFeatureFlags = (): FeatureFlags => {
  const ldFlags = useFlags();
  const ldFlagsReady = Object.keys(ldFlags).length > 0;

  return useMemo(() => {
    const flags: Record<string, boolean | number> = {};
    Object.values(FeatureFlagKey).forEach((key) => {
      const ldRaw = ldFlags[key];
      warnIfUndefined(key as FeatureFlagKey, ldRaw, ldFlagsReady);
      flags[key] = ldRaw ?? false;
    });
    return flags as FeatureFlags;
  }, [ldFlags, ldFlagsReady]);
};

/**
 * Hook to check a single feature flag.
 *
 * Supports both boolean and number flag types with compile-time type inference
 * and runtime validation. The return type is automatically inferred from the
 * default value provided. For local overrides, use the LaunchDarkly Developer Toolbar.
 *
 * @param flagKey - The feature flag key to check
 * @param defaultValue - Default value if flag is not found (defaults to false)
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   // Boolean flag (type inferred automatically)
 *   const isEnabled = useFeatureFlag(FeatureFlagKey.EXAMPLE_FEATURE);
 *   const isEnabled2 = useFeatureFlag(FeatureFlagKey.EXAMPLE_FEATURE, false);
 *
 *   // Number flag (type inferred automatically)
 *   const maxRetries = useFeatureFlag(FeatureFlagKey.MAX_RETRIES, 3);
 *
 *   return isEnabled ? <NewFeature /> : <OldFeature />;
 * }
 * ```
 */
export function useFeatureFlag(flagKey: FeatureFlagKey, defaultValue?: boolean): boolean;
export function useFeatureFlag(flagKey: FeatureFlagKey, defaultValue: number): number;
export function useFeatureFlag(
  flagKey: FeatureFlagKey,
  defaultValue: boolean | number = false
): boolean | number {
  const ldFlags = useFlags();
  const ldFlagsReady = Object.keys(ldFlags).length > 0;

  const ldRaw = ldFlags[flagKey];
  warnIfUndefined(flagKey, ldRaw, ldFlagsReady);
  const flagValue = ldRaw ?? defaultValue;

  if (isNumber(defaultValue)) {
    // User expects a number flag
    if (isNumber(flagValue)) {
      return flagValue;
    }
    throw new Error(
      `Feature flag ${flagKey} expected number but got ${typeof flagValue}: ${flagValue}`
    );
  }

  // User expects a boolean flag
  return flagValue as boolean;
}

/**
 * Signals when LaunchDarkly has flags for the authenticated user.
 *
 * useFlags() returns {} until LD identify()s the auth user; consumers that
 * resolve flag-dependent state during that window see all-false defaults.
 * Short-circuits to true when LD is disabled or has timed out, so the app
 * never stalls if LD is misconfigured or unreachable.
 */
export function useLaunchDarklyReady(): boolean {
  const ldFlags = useFlags();
  const { isAuthenticated, isLoading } = useAuth0();
  const [timedOut, setTimedOut] = useState(false);

  // In passthrough mode (no LDProvider) useFlags() returns {} forever.
  const ldDisabled = !import.meta.env.VITE_LAUNCHDARKLY_CLIENT_SIDE_ID;

  useEffect(() => {
    if (ldDisabled) return;
    const timer = setTimeout(() => setTimedOut(true), LD_READY_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [ldDisabled]);

  if (ldDisabled || timedOut) return true;
  return !isLoading && isAuthenticated && Object.keys(ldFlags).length > 0;
}
